import * as React from "react";
import { useState } from "react";
import { Formik, Form } from "formik";
import styled from "styled-components";
import { PluginStore } from "graylog-web-plugin/plugin";
import { Select } from "components/common";
import { defaultCompare } from "logic/DefaultCompare";
import {
  Input,
  Alert,
  Button,
  ButtonToolbar,
  Col,
  ControlLabel,
  FormGroup,
  HelpBlock,
  Row
} from "components/bootstrap";
import InputConfigurationForm from "./InputConfigurationForm";
const formatInputTypeOptions = (inputTypes) => {
  const inputTypesEntries = Object.entries(inputTypes);
  const options = inputTypesEntries.map(([id, label]) => ({ value: id, label, disabled: false }));
  options.sort((inputTypeA, inputTypeB) => defaultCompare(inputTypeA.label, inputTypeB.label));
  if (options.length === 0) {
    options.push({ value: "none", label: "No inputs available", disabled: true });
  }
  return options;
};
const CustomInputWrapper = styled.div`
  padding: 15px;
`;
const defaultFormValues = { type: void 0, title: "", configuration: {} };
const InputForm = ({
  action = "create",
  input = void 0,
  inputTypes,
  inputDescriptions,
  onCancel,
  onSubmit
}) => {
  const [configurationFieldStates, setConfigurationFieldStates] = useState({});
  const isConfigurationFieldDirty = (field) => {
    if (!configurationFieldStates[field]) {
      return false;
    }
    return configurationFieldStates[field] && configurationFieldStates[field].dirty;
  };
  const encryptedSubmitValues = (type, configurationValues) => {
    if (!type || !configurationValues) {
      return configurationValues;
    }
    const newEncryptedConfigurationValues = {};
    const cleanFields = Object.keys(configurationValues).filter((field) => !isConfigurationFieldDirty(field));
    const encryptedCleanFields = cleanFields.filter(
      (field) => inputDescriptions[type].requested_configuration[field].is_encrypted
    );
    encryptedCleanFields.forEach((field) => {
      if (configurationValues[field].is_set !== void 0) {
        newEncryptedConfigurationValues[field] = { keep_value: true };
      }
    });
    return { ...configurationValues, ...newEncryptedConfigurationValues };
  };
  const handleSubmit = ({ type, title, configuration }) => {
    const configurationValues = encryptedSubmitValues(type, configuration);
    onSubmit({ type, title, configuration: configurationValues });
  };
  const _getCustomInputComponent = (inputType) => {
    const customConfiguration = PluginStore.exports("inputConfiguration").find(
      (inputConfig) => inputConfig.type === inputType
    );
    return customConfiguration == null ? void 0 : customConfiguration.embeddedComponent;
  };
  let initialValues;
  if (action === "edit" && input) {
    const { type, title, configuration } = input;
    initialValues = { type, title, configuration };
  } else {
    initialValues = defaultFormValues;
  }
  const handleConfigurationFieldChange = (field, value, setFieldValue, dirty = true) => {
    const configurationFieldState = configurationFieldStates[field];
    setConfigurationFieldStates({ ...configurationFieldStates, [field]: { ...configurationFieldState, ...{ dirty } } });
    setFieldValue(`configuration.${field}`, value);
  };
  return /* @__PURE__ */ React.createElement(Formik, { initialValues, onSubmit: handleSubmit }, ({ values, handleChange, setFieldValue }) => {
    const inputDescription = values.type ? inputDescriptions[values.type] : void 0;
    const CustomInputComponent = action === "create" && values.type && _getCustomInputComponent(values.type);
    return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, null, action === "create" && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "inputType" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Input Type"), /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "inputType",
        name: "inputType",
        placeholder: "Select Input Type",
        options: formatInputTypeOptions(inputTypes),
        onChange: (option) => {
          setFieldValue("type", option);
          setFieldValue("configuration", {});
        },
        value: values.type
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Select the Input type you want to create.")), inputDescription && (CustomInputComponent ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "This Input provides a custom form, follow the steps shown below to create the Forwarder Input.") : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "title",
        name: "title",
        label: "Title",
        type: "text",
        value: values.title,
        onChange: handleChange,
        help: "Meaningful name used to identify this Input.",
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      InputConfigurationForm,
      {
        type: values.type,
        handleConfigurationChange: (field, value, dirty) => handleConfigurationFieldChange(field, value, setFieldValue, dirty),
        inputDescription,
        setDefaultValues: (defaultValues) => setFieldValue("configuration", defaultValues),
        configurationFieldStates,
        values: values.configuration
      }
    ))), !CustomInputComponent && /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { onClick: onCancel }, "Cancel"), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", type: "submit" }, action === "create" ? "Create" : "Update", " Input")))), CustomInputComponent && /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement(CustomInputWrapper, null, /* @__PURE__ */ React.createElement(CustomInputComponent, { onSubmit: handleSubmit }))));
  });
};
export default InputForm;
